import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PRODUCTS, SIZES, formatPrice } from "@/data/products";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ product, index, onAdd }) => {
  const oneSize = Array.isArray(product.variants) && product.variants.length > 0 && product.variants.every((v) => !v.size);
  const soldOut = product.stock === 0;
  return (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
    data-testid={`product-card-${product.id}`}
    className="group border border-zinc-200 bg-white transition-all duration-300 hover:border-tiger hover:shadow-[0_0_30px_rgba(255,85,0,0.15)]"
  >
    <div className="relative overflow-hidden">
      <Link to={`/gb/en/product/${product.id}`} data-testid={`product-link-${product.id}`}>
        <img
          src={product.image}
          alt={product.title}
          className="h-72 w-full object-cover transition-opacity duration-300 group-hover:opacity-0"
        />
        <img
          src={product.secondaryImage}
          alt={`${product.title} alternate view`}
          className="absolute inset-0 h-72 w-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        />
      </Link>
      <span className={`absolute left-3 top-3 px-2.5 py-1 font-heading text-[10px] font-bold uppercase tracking-widest ${soldOut ? "bg-zinc-900 text-white" : "bg-tiger text-black"}`}>
        {soldOut ? "Sold Out" : product.badge}
      </span>
      <div
        data-testid={`quick-add-${product.id}`}
        className="absolute inset-x-0 bottom-0 translate-y-full border-t border-zinc-200 bg-white/95 py-3 backdrop-blur-sm transition-transform duration-300 group-hover:translate-y-0"
      >
        {soldOut ? (
          <p className="py-2 text-center font-heading text-xs font-bold uppercase tracking-widest text-zinc-400" data-testid={`sold-out-${product.id}`}>
            Sold out — restocking soon
          </p>
        ) : (
          <>
            <p className="mb-2 text-center font-body text-[10px] uppercase tracking-widest text-zinc-500">
              {oneSize ? "Quick add" : "Quick add — pick a size"}
            </p>
            <div className="flex justify-center gap-2">
              {oneSize ? (
                <button
                  data-testid={`add-to-cart-btn-${product.id}-os`}
                  onClick={() => onAdd(product, "OS")}
                  className="flex h-8 items-center border border-zinc-300 px-4 font-heading text-xs font-semibold text-zinc-800 transition-colors hover:border-tiger hover:bg-tiger hover:text-black"
                >
                  One Size
                </button>
              ) : (
                SIZES.map((size) => (
                  <button
                    key={size}
                    data-testid={`add-to-cart-btn-${product.id}-${size.toLowerCase()}`}
                    onClick={() => onAdd(product, size)}
                    className="flex h-8 w-9 items-center justify-center border border-zinc-300 font-heading text-xs font-semibold text-zinc-800 transition-colors hover:border-tiger hover:bg-tiger hover:text-black"
                  >
                    {size}
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
    <div className="p-4">
      <p className="font-body text-[10px] uppercase tracking-widest text-zinc-400">{product.category}</p>
      <Link to={`/gb/en/product/${product.id}`}>
        <h3 className="mt-1 font-heading text-sm font-semibold text-zinc-900 transition-colors hover:text-tiger">{product.title}</h3>
      </Link>
      <p className="mt-2 font-heading text-base font-bold text-zinc-900 transition-colors group-hover:text-tiger">
        {formatPrice(product.price)}
      </p>
    </div>
  </motion.div>
  );
};

export const ProductGrid = () => {
  const { add } = useCart();
  const [products, setProducts] = useState(PRODUCTS);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setProducts(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="shop" data-testid="product-grid-section" className="bg-white py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="mb-3 flex items-center gap-3 font-body text-xs font-medium uppercase tracking-[0.3em] text-tiger">
            <span className="h-px w-8 bg-tiger" />Drop 01 — Live
          </p>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-zinc-900 sm:text-3xl lg:text-4xl">
            Latest Fierce Drops
          </h2>
          <p className="mt-3 max-w-lg font-body text-sm text-zinc-500">
            Every piece is printed on demand and fulfilled through Printful — no waste, no dead stock, just fresh gear.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} onAdd={add} />
          ))}
        </div>
      </div>
    </section>
  );
};
